import React, { Suspense, useEffect } from "react";
import { SignedOut, SignedInOrRedirect, Provider, useUser, useFindMany } from "@gadgetinc/react";
import { Outlet, Route, RouterProvider, createBrowserRouter, createRoutesFromElements, useNavigate, useSearchParams, Navigate, useRouteError, isRouteErrorResponse } from "react-router-dom";
import "./App.css";
import { api } from "./api";
import RequireSignedInRole from "./components/RequireSignedInRole";
import ThemeProvider from "./components/ThemeProvider";
import RootPageHandler from "./components/RootPageHandler";
// import FloatingAdminAssistant from "./components/FloatingAdminAssistant"; // Temporarily disabled to reduce log clutter

// Check if we're in development mode
const isDevelopment = import.meta.env.MODE === 'development' || import.meta.env.NODE_ENV === 'development';
import SignedInPage from "./routes/index";
import AdminDashboard from "./routes/admin";
import ChatInterface from "./routes/chat.$assistantId";
import AdminAccessCodes from "./routes/admin.access-codes";
import AdminUsers from "./routes/admin.users";
import AdminUserApprovals from "./routes/admin.user-approvals";
import AdminAnalytics from "./routes/admin.analytics";
import AdminEmbedTokens from "./routes/admin.embed-tokens";
import AdminFineTuning from "./routes/admin.fine-tuning";
import AdminAgentFineTuning from "./routes/admin.agent-fine-tuning";
import AdminAgentChat from "./routes/admin.agent-chat";
import AdminInvite from "./routes/admin.invite";
import AdminInstitutions from "./routes/admin.institutions";
import AdminClinicalSites from "./routes/admin.clinical-sites";
import AdminClinicalDocs from "./routes/admin.clinical-docs";
import AdminShiftSubmission from "./routes/admin.shift-submission";
import AdminHITLQueue from "./routes/admin.hitl-queue";
import ECGGeneratorTest from "./routes/admin.ecg-generator";
import AdminCalendarSync from "./routes/admin.calendar-sync";

import AdminRequests from "./routes/admin.requests";
import AdminAgentsTest from "./routes/admin.agents-test";
import AgentControlPanel from "./routes/admin.agent-control-panel";
import AdminVMAgents from "./routes/admin.vm-agents";
import FileManager from "./routes/admin.file-manager";
import InstitutionConfigPage from "./routes/admin.institution-config.$institution";
import Agents from "./routes/agents";
import AgentChat from "./routes/agents.$agentName";
import VMAgentChat from "./routes/agents.vm.$agentId";
import CreateAgent from "./routes/create-agent";
import DocumentProcessing from "./routes/document-processing";
import PatientEncounters from "./routes/patient-encounters";
import Shifts from "./routes/shifts";
import NewShift from "./routes/shifts.new";
import ShiftDetails from "./routes/shift-details.$shiftId";
import Calendar from "./routes/calendar";
import EducationalContent from "./routes/educational-content";
import TestClinicalDocs from "./routes/test-clinical-docs";
import ClinicalDocumentReader from "./routes/clindocsreader";
import DocumentProcessorConfigurator from "./routes/document-processor-configurator";
import AISystemGenerator from "./routes/ai-system-generator";
import FisdapTest from "./routes/fisdap-test";
import HITLReview from "./routes/fisdap-review";
import FisdapReports from "./routes/fisdap-reports";
import QuestionBank from "./routes/question-bank";
import AddressLookup from "./routes/address-lookup";
import ScenarioGenerator from "./routes/scenario-generator";
import ImmersiveScenarioGenerator from "./routes/immersive-scenario-generator";
import MyScenarios from "./routes/my-scenarios";
import MyEvaluations from "./routes/my-scenarios.evaluations";
import MySessions from "./routes/my-scenarios.sessions";
import ScenarioShareWithBoundary from "./routes/scenario-share-wrapper";
import ScenarioViewer from "./routes/scenario.$id";
import VoiceScenarioPage from './routes/voice-scenario.$scenarioId';
import LiveAudioJoinPage from "./routes/live-audio.$token";
import SignInPage from './routes/sign-in';
import ForgotPasswordPage from './routes/forgot-password';
import ResetPasswordPage from './routes/reset-password';
import PasswordSetupPage from './routes/password-setup';
import SignOutPage from './routes/sign-out';
import VoiceSimToken from './routes/voice-sim.$token';
import MedicalControl from './routes/medical-control';
import MICULayout from './components/micu/MICULayout';
import MICUCaseList from './routes/micu-mobile';
import MICUNewCase from './routes/micu-mobile.new';
import MICUCaseDashboard from './routes/micu-mobile.case.$id';
import MICUPatient from './routes/micu-mobile.case.$id.patient';
import MICUDeviceDetail from './routes/micu-mobile.case.$id.device.$deviceId';
import MICUMedDetail from './routes/micu-mobile.case.$id.med.$medId';
const AdminFormsLibrary = React.lazy(() => import('./routes/admin.forms-library'));
const AdminShiftPacks = React.lazy(() => import('./routes/admin.shift-packs'));
const FormUpload = React.lazy(() => import('./routes/form-upload.$token'));
const EvalForm = React.lazy(() => import('./routes/eval-form.$token'));
const ShiftCompliance = React.lazy(() => import('./routes/shift-compliance'));
const StudentCompliance = React.lazy(() => import('./routes/student-compliance.$studentId'));
const EvalBuilder = React.lazy(() => import('./routes/eval-builder'));
const EvalSystems = React.lazy(() => import('./routes/eval-systems'));
const EvalSystemDetail = React.lazy(() => import('./routes/eval-system.$id'));
const EvalResults = React.lazy(() => import('./routes/eval-results.$systemId'));

const AuthPageHandler = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  useEffect(() => {
    // Check for invitation-specific parameters
    const hasInvitationToken = searchParams.has('token');
    const hasInvitationEmail = searchParams.has('email');
    const hasInvitationData = searchParams.has('invitation');
    
    // If no invitation-specific data is found, redirect to home
    if (!hasInvitationToken && !hasInvitationEmail && !hasInvitationData) {
      navigate('/');
      return;
    }
  }, [navigate, searchParams]);
  
  // If we have invitation data, show the sign-in interface
  return <RootPageHandler />;
};

const ScenarioShareRouteError = () => {
  const error = useRouteError();

  let details = "Unknown route error";
  if (isRouteErrorResponse(error)) {
    details = `${error.status} ${error.statusText}${error.data ? `: ${JSON.stringify(error.data)}` : ""}`;
  } else if (error instanceof Error) {
    details = error.message;
  } else if (error) {
    details = String(error);
  }

  console.error("Scenario share route error:", error);

  return (
    <div style={{ padding: "40px", textAlign: "center" }}>
      <h2>Error Loading Scenario</h2>
      <p>There was an error loading this scenario.</p>
      <pre style={{ whiteSpace: "pre-wrap", textAlign: "left", maxWidth: "900px", margin: "20px auto", padding: "12px", background: "#111", color: "#eee", borderRadius: "6px" }}>
        {details}
      </pre>
    </div>
  );
};

const RequireScenarioGeneratorAccess = ({ children }) => {
  const user = useUser();

  // Fetch user's active cohort memberships
  const [{ data: memberships, fetching }] = useFindMany(
    api.cohortMembership,
    {
      filter: {
        AND: [
          { userId: { equals: user?.id } },
          { active: { equals: true } }
        ]
      },
      select: {
        id: true,
        hasScenarioGeneratorAccess: true,
        primaryRole: true
      },
      pause: !user?.id
    }
  );

  // Show loading state while fetching
  if (!user || fetching) return null;

  // Check if user has admin or system-admin role
  const hasAdminRole = user.roles?.some(role => 
    role.key === 'admin' || role.key === 'system-admin' ||
    role === 'admin' || role === 'system-admin'
  );

  if (hasAdminRole) return children;

  // Check if user has scenario generator access via any active cohort membership
  // Either explicitly granted OR has instructor role
  const hasScenarioAccess = memberships?.some(membership => 
    membership.hasScenarioGeneratorAccess === true ||
    membership.primaryRole === 'instructor'
  );

  if (!hasScenarioAccess) {
    // User doesn't have permission, redirect to sign-in page
    return <Navigate to="/sign-in" replace />;
  }

  return children;
};

const App = () => {
  // Function to create admin routes
  const createAdminRoutes = () => {
    return (
      <>
        <Route
          path="admin"
          element={
            <SignedInOrRedirect>
              <AdminDashboard />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/access-codes"
          element={
            <SignedInOrRedirect>
              <AdminAccessCodes />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/users"
          element={
            <SignedInOrRedirect>
              <AdminUsers />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/user-approvals"
          element={
            <SignedInOrRedirect>
              <AdminUserApprovals />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/analytics"
          element={
            <SignedInOrRedirect>
              <AdminAnalytics />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/embed-tokens"
          element={
            <SignedInOrRedirect>
              <AdminEmbedTokens />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/fine-tuning"
          element={
            <SignedInOrRedirect>
              <AdminFineTuning />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/agent-fine-tuning"
          element={
            <SignedInOrRedirect>
              <AdminAgentFineTuning />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/agent-chat"
          element={
            <SignedInOrRedirect>
              <AdminAgentChat />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/invite"
          element={
            <SignedInOrRedirect>
              <AdminInvite />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/institution-config/:institution"
          element={
            <SignedInOrRedirect>
              <InstitutionConfigPage />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/institutions"
          element={
            <SignedInOrRedirect>
              <AdminInstitutions />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/clinical-sites"
          element={
            <SignedInOrRedirect>
              <AdminClinicalSites />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/clinical-docs"
          element={
            <SignedInOrRedirect>
              <AdminClinicalDocs />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/shift-submission"
          element={
            <SignedInOrRedirect>
              <AdminShiftSubmission />
            </SignedInOrRedirect>
          }
        />

        <Route
          path="admin/requests"
          element={
            <SignedInOrRedirect>
              <AdminRequests />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/agents-test"
          element={
            <SignedInOrRedirect>
              <AdminAgentsTest />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/agent-control-panel"
          element={
            <SignedInOrRedirect>
              <AgentControlPanel />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/vm-agents"
          element={
            <SignedInOrRedirect>
              <AdminVMAgents />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/file-manager"
          element={
            <SignedInOrRedirect>
              <FileManager />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/ai-system-generator"
          element={
            <SignedInOrRedirect>
              <AISystemGenerator />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/hitl-queue"
          element={
            <SignedInOrRedirect>
              <AdminHITLQueue />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/ecg-generator"
          element={
            <SignedInOrRedirect>
              <ECGGeneratorTest />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/calendar-sync"
          element={
            <SignedInOrRedirect>
              <AdminCalendarSync />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/forms-library"
          element={
            <SignedInOrRedirect>
              <React.Suspense fallback={<div style={{color:'white',textAlign:'center',padding:'4rem'}}>Loading...</div>}>
                <AdminFormsLibrary />
              </React.Suspense>
            </SignedInOrRedirect>
          }
        />
        <Route
          path="admin/shift-packs"
          element={
            <SignedInOrRedirect>
              <React.Suspense fallback={<div style={{color:'white',textAlign:'center',padding:'4rem'}}>Loading...</div>}>
                <AdminShiftPacks />
              </React.Suspense>
            </SignedInOrRedirect>
          }
        />
      </>
    );
  };

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        {/* Public scenario-share route - accessible without user session authentication */}
        <Route
          path="scenario-share/:token"
          element={
            <PublicWithProviderLayout>
              <ScenarioShareWithBoundary />
            </PublicWithProviderLayout>
          }
          errorElement={<ScenarioShareRouteError />}
        />
        
        {/* Public voice-sim route - accessible without authentication */}
        <Route
          path="voice-sim/:token"
          element={
            <PublicWithProviderLayout>
              <React.Suspense fallback={<div>Loading...</div>}>
                <VoiceSimToken />
              </React.Suspense>
            </PublicWithProviderLayout>
          }
          errorElement={
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <h2>Error Loading Voice Simulation</h2>
              <p>There was an error loading this voice simulation invitation. Please try refreshing the page or contact your instructor for a new link.</p>
            </div>
          }
        />
        
        {/* Public eval-form route - digital evaluation form via QR (no auth) */}
        <Route
          path="eval-form/:token"
          element={
            <PublicWithProviderLayout>
              <React.Suspense fallback={<div>Loading...</div>}>
                <EvalForm />
              </React.Suspense>
            </PublicWithProviderLayout>
          }
          errorElement={
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <h2>Error Loading Form</h2>
              <p>There was an error loading this evaluation form. Please try refreshing the page or contact your instructor for a new link.</p>
            </div>
          }
        />

        {/* Public voice-scenario route - accessible for both logged-in users and tokenized access */}
        <Route
          path="voice-scenario/:scenarioId"
          element={
            <PublicWithProviderLayout>
              <React.Suspense fallback={<div>Loading...</div>}>
                <VoiceScenarioPage />
              </React.Suspense>
            </PublicWithProviderLayout>
          }
          errorElement={
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <h2>Error Loading Voice Scenario</h2>
              <p>There was an error loading this voice scenario. Please try refreshing the page or contact your instructor.</p>
            </div>
          }
        />

        <Route
          path="live-audio/:token"
          element={
            <PublicWithProviderLayout>
              <React.Suspense fallback={<div>Loading...</div>}>
                <LiveAudioJoinPage />
              </React.Suspense>
            </PublicWithProviderLayout>
          }
          errorElement={
            <div style={{ padding: "40px", textAlign: "center" }}>
              <h2>Error Loading Live Audio Capture</h2>
              <p>There was an error loading this live audio capture link. Please request a new link from the evaluator.</p>
            </div>
          }
        />
        
        {/* MICU Medic Mobile — self-contained module with own theme */}
        <Route path="micu-mobile" element={<MICULayout />}>
          <Route index element={<MICUCaseList />} />
          <Route path="new" element={<MICUNewCase />} />
          <Route path="case/:id" element={<MICUCaseDashboard />} />
          <Route path="case/:id/patient" element={<MICUPatient />} />
          <Route path="case/:id/device/:deviceId" element={<MICUDeviceDetail />} />
          <Route path="case/:id/med/:medId" element={<MICUMedDetail />} />
        </Route>

        {/* All authenticated routes wrapped in Layout/Provider */}
        <Route path="/" element={<Layout />}>
        <Route
          index
          element={
            <SignedInOrRedirect>
              <SignedInPage />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="signed-in"
          element={<Navigate to="/" replace />}
        />
        <Route
          path="auth"
          element={<AuthPageHandler />}
        />
        {createAdminRoutes()}
        <Route
          path="chat/:assistantId"
          element={
            <SignedInOrRedirect>
              <ChatInterface />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="agents"
          element={
            <SignedInOrRedirect>
              <RequireSignedInRole role={["admin", "system-admin"]}>
                <Agents />
              </RequireSignedInRole>
            </SignedInOrRedirect>
          }
        />
        <Route
          path="agents/:agentName"
          element={
            <SignedInOrRedirect>
              <AgentChat />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="agents/vm/:agentId"
          element={
            <SignedInOrRedirect>
              <VMAgentChat />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="create-agent"
          element={
            <SignedInOrRedirect>
              <RequireSignedInRole role={["admin", "system-admin"]}>
                <CreateAgent />
              </RequireSignedInRole>
            </SignedInOrRedirect>
          }
        />
        <Route
          path="document-processing"
          element={
            <SignedInOrRedirect>
              <DocumentProcessing />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="document-processor-configurator"
          element={
            <SignedInOrRedirect>
              <DocumentProcessorConfigurator />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="shifts"
          element={
            <SignedInOrRedirect>
              <Shifts />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="shifts/new"
          element={
            <SignedInOrRedirect>
              <NewShift />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="shift-details/:shiftId"
          element={
            <SignedInOrRedirect>
              <ShiftDetails />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="calendar"
          element={
            <SignedInOrRedirect>
              <Calendar />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="patient-encounters/:shiftId?"
          element={
            <SignedInOrRedirect>
              <PatientEncounters />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="educational-content"
          element={
            <SignedInOrRedirect>
              <EducationalContent />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="test-clinical-docs"
          element={
            <SignedInOrRedirect>
              <TestClinicalDocs />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="clindocsreader"
          element={
            <SignedInOrRedirect>
              <ClinicalDocumentReader />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="fisdap-test"
          element={
            <SignedInOrRedirect>
              <FisdapTest />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="fisdap-review"
          element={
            <SignedInOrRedirect>
              <HITLReview />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="fisdap-reports"
          element={
            <SignedInOrRedirect>
              <FisdapReports />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="question-bank"
          element={
            <SignedInOrRedirect>
              <QuestionBank />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="address-lookup"
          element={
            <SignedInOrRedirect>
              <AddressLookup />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="medical-control"
          element={
            <SignedInOrRedirect>
              <RequireSignedInRole role={["system-admin"]}>
                <MedicalControl />
              </RequireSignedInRole>
            </SignedInOrRedirect>
          }
        />
        <Route
          path="eval-builder"
          element={
            <SignedInOrRedirect>
              <React.Suspense fallback={<div style={{color:'white',textAlign:'center',padding:'4rem'}}>Loading...</div>}>
                <EvalBuilder />
              </React.Suspense>
            </SignedInOrRedirect>
          }
        />
        <Route
          path="eval-systems"
          element={
            <SignedInOrRedirect>
              <React.Suspense fallback={<div style={{color:'white',textAlign:'center',padding:'4rem'}}>Loading...</div>}>
                <EvalSystems />
              </React.Suspense>
            </SignedInOrRedirect>
          }
        />
        <Route
          path="eval-system/:id"
          element={
            <SignedInOrRedirect>
              <React.Suspense fallback={<div style={{color:'white',textAlign:'center',padding:'4rem'}}>Loading...</div>}>
                <EvalSystemDetail />
              </React.Suspense>
            </SignedInOrRedirect>
          }
        />
        <Route
          path="form-upload/:token"
          element={
            <SignedInOrRedirect>
              <React.Suspense fallback={<div style={{color:'white',textAlign:'center',padding:'4rem'}}>Loading...</div>}>
                <FormUpload />
              </React.Suspense>
            </SignedInOrRedirect>
          }
        />
        <Route
          path="shift-compliance"
          element={
            <SignedInOrRedirect>
              <React.Suspense fallback={<div style={{color:'white',textAlign:'center',padding:'4rem'}}>Loading...</div>}>
                <ShiftCompliance />
              </React.Suspense>
            </SignedInOrRedirect>
          }
        />
        <Route
          path="student-compliance/:studentId"
          element={
            <SignedInOrRedirect>
              <React.Suspense fallback={<div style={{color:'white',textAlign:'center',padding:'4rem'}}>Loading...</div>}>
                <StudentCompliance />
              </React.Suspense>
            </SignedInOrRedirect>
          }
        />
        <Route
          path="eval-results/:systemId"
          element={
            <SignedInOrRedirect>
              <React.Suspense fallback={<div style={{color:'white',textAlign:'center',padding:'4rem'}}>Loading...</div>}>
                <EvalResults />
              </React.Suspense>
            </SignedInOrRedirect>
          }
        />
        <Route
          path="scenario-generator"
          element={
            <SignedInOrRedirect>
              <RequireScenarioGeneratorAccess>
                <ScenarioGenerator />
              </RequireScenarioGeneratorAccess>
            </SignedInOrRedirect>
          }
        />
        <Route
          path="immersive-scenario-generator"
          element={
            <SignedInOrRedirect>
              <RequireScenarioGeneratorAccess>
                <ImmersiveScenarioGenerator />
              </RequireScenarioGeneratorAccess>
            </SignedInOrRedirect>
          }
        />
        <Route
          path="my-scenarios"
          element={
            <SignedInOrRedirect>
              <RequireScenarioGeneratorAccess>
                <MyScenarios />
              </RequireScenarioGeneratorAccess>
            </SignedInOrRedirect>
          }
        />
        <Route
          path="my-scenarios/evaluations"
          element={
            <SignedInOrRedirect>
              <RequireScenarioGeneratorAccess>
                <MyEvaluations />
              </RequireScenarioGeneratorAccess>
            </SignedInOrRedirect>
          }
        />
        <Route
          path="my-scenarios/sessions"
          element={
            <SignedInOrRedirect>
              <MySessions />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="scenario/:id"
          element={
            <SignedInOrRedirect>
              <ScenarioViewer />
            </SignedInOrRedirect>
          }
        />
        <Route
          path="sign-in"
          element={
            <SignedOut>
              <SignInPage />
            </SignedOut>
          }
        />
        <Route
          path="forgot-password"
          element={
            <SignedOut>
              <ForgotPasswordPage />
            </SignedOut>
          }
        />
        <Route
          path="reset-password"
          element={
            <SignedOut>
              <ResetPasswordPage />
            </SignedOut>
          }
        />
        <Route
          path="setup-password"
          element={
            <SignedOut>
              <PasswordSetupPage />
            </SignedOut>
          }
        />
        <Route
          path="password-setup"
          element={
            <SignedOut>
              <PasswordSetupPage />
            </SignedOut>
          }
        />
        <Route
          path="sign-out"
          element={<SignOutPage />}
        />
        <Route path="*" element={<div>Page not found. Please check the URL and try again.</div>} />
        </Route>
      </>
    )
  );

  return (
    <ThemeProvider>
      <RouterProvider router={router} />
    </ThemeProvider>
  );
};

const PublicWithProviderLayout = ({ children }) => {
  const navigate = useNavigate();

  return (
    <Provider 
      api={api} 
      navigate={navigate}
      auth={{ signInPath: "/sign-in" }}
    >
      <Suspense fallback={<div>Loading...</div>}>
        {children}
      </Suspense>
    </Provider>
  );
};

const Layout = () => {
  const navigate = useNavigate();

  return (
    <Provider 
      api={api} 
      navigate={navigate}
      auth={{ signInPath: "/sign-in" }}
    >
      <Suspense fallback={<div>Loading...</div>}>
        <div className="app">
          {isDevelopment && (
            <div style={{
              position: 'fixed',
              top: 0,
              right: 0,
              backgroundColor: '#ff6b6b',
              color: 'white',
              padding: '4px 8px',
              fontSize: '12px',
              zIndex: 9999,
              borderBottomLeftRadius: '4px',
              fontWeight: 'bold'
            }}>
              DEV
            </div>
          )}
          <div className="app-content">
            <div className="main">
              <Outlet />
            </div>
          </div>
          <SignedInOrRedirect>
            {/* <FloatingAdminAssistant /> */} {/* Temporarily disabled to stop background requests cluttering logs */}
          </SignedInOrRedirect>
        </div>
      </Suspense>
    </Provider>
  );
};

export default App;
